import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductDesignerConfig, PrintColorOption } from '@customedge/shared';
import { DesignPreviewCanvas } from './DesignPreviewCanvas';
import { ErrorBoundary } from './ErrorBoundary';

// Read-only, login-free 2D/3D preview for the customer approval page — reuses
// the exact same PreviewView (2D, grid/slider) and Model3DPreview (3D, orbit +
// zoom +/- + rotate) the real designer uses, so the customer sees the design on
// the true product photo / 3D wrap with proper zoom, not a single flat image.
//
// The trick that lets us reuse those components with no live editing canvas
// mounted: both composite from `sideColorCountJsonCache` (via
// snapshotCanvasJson in lib/designDownload.ts) whenever a side has no live
// Fabric canvas — and `loadFromTemplate` populates exactly that cache. So this
// only needs to (1)
// boot the product config into the store like the designer does
// (useProductSetup: sides, productConfig incl. glbModelUrl, variants) and (2)
// load the saved design JSON into the cache. No <DesignerWorkspace> required.
export function ApprovalPreview({
  designId, sku, jsonObjects, selectedVariantOptions, colorVariantId, printMethod,
  printPositions, printMethodByPosition, printColorByPosition,
}: {
  // Set into the shared designStore (via DesignPreviewCanvas) so PreviewView's
  // embroidery/embossing/laser-engraving/doming status polling — which keys
  // off store.savedDesignId — actually runs on this page. Without it those
  // four async-render modules silently never appeared here, always falling
  // back to the plain flat client render even once DONE server-side (found
  // 2026-09-01).
  designId: string;
  sku: string;
  jsonObjects: Record<string, any>;
  // The customer's saved Object Variant picks — applied to the store so the
  // 3D wrap (and any 2D variant preview overlay) renders the variant they
  // chose, not the model's default appearance.
  selectedVariantOptions?: Record<string, string>;
  // The customer's saved vendor Product Color Variant + print method — see
  // DesignPreviewCanvas's own comment for why these are needed separately
  // from selectedVariantOptions.
  colorVariantId?: string | null;
  printMethod?: string | null;
  printPositions?: string[];
  printMethodByPosition?: Record<string, string>;
  printColorByPosition?: Record<string, PrintColorOption>;
}) {
  const { t } = useTranslation();
  const [config, setConfig] = useState<ProductDesignerConfig | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/products/${encodeURIComponent(sku)}/config`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(data => { if (!cancelled) setConfig(data); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [sku]);

  if (failed) {
    return <div style={fallbackStyle}>{t('approval.previewUnavailable')}</div>;
  }
  if (!config) {
    return <div style={fallbackStyle}>{t('approval.previewLoading')}</div>;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<div style={fallbackStyle}>{t('approval.previewLoading')}</div>}>
        <ApprovalPreviewInner
          designId={designId}
          config={config}
          jsonObjects={jsonObjects}
          selectedVariantOptions={selectedVariantOptions}
          colorVariantId={colorVariantId}
          printMethod={printMethod}
          printPositions={printPositions}
          printMethodByPosition={printMethodByPosition}
          printColorByPosition={printColorByPosition}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function ApprovalPreviewInner({
  designId, config, jsonObjects, selectedVariantOptions, colorVariantId, printMethod,
  printPositions, printMethodByPosition, printColorByPosition,
}: {
  designId: string;
  config: ProductDesignerConfig;
  jsonObjects: Record<string, any>;
  selectedVariantOptions?: Record<string, string>;
  colorVariantId?: string | null;
  printMethod?: string | null;
  printPositions?: string[];
  printMethodByPosition?: Record<string, string>;
  printColorByPosition?: Record<string, PrintColorOption>;
}) {
  // No `minHeight` here — this fills exactly whatever height its parent
  // column gives it (see ApprovalPage.tsx's `leftColStyle`), including on
  // narrow/mobile widths where that column is deliberately capped short. A
  // `minHeight` larger than that cap previously forced this box taller than
  // its clipping ancestor, and the WebGL <canvas> inside `DesignPreviewCanvas`
  // painted past the clip boundary into the message thread below it.
  return (
    <div style={{ height: '100%' }}>
      <DesignPreviewCanvas
        designId={designId}
        config={config}
        jsonObjects={jsonObjects}
        selectedVariantOptions={selectedVariantOptions}
        colorVariantId={colorVariantId}
        printMethod={printMethod}
        printPositions={printPositions}
        printMethodByPosition={printMethodByPosition}
        printColorByPosition={printColorByPosition}
      />
    </div>
  );
}

const fallbackStyle: React.CSSProperties = {
  height: '100%', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: '#94a3b8', fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 8,
};
